import { useEffect, useState } from 'react';
import {
  Box, AppBar, Toolbar, IconButton,
  useTheme, alpha, Stack, Tooltip,
} from '@mui/material';
import { Menu as MenuIcon, MenuOpen, WbSunny as LightIcon, DarkMode as DarkIcon } from '@mui/icons-material';
import { Outlet, Link as RouterLink } from 'react-router-dom';
import { Sidebar, DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED } from './Sidebar';
import { NotificationBell } from './NotificationBell';
import { CreditsWalletChip } from '../CreditsWalletChip';
import { MoneyWalletChip } from '../MoneyWalletChip';
import { ImpersonationBanner } from './ImpersonationBanner';
import { TenantAccessBar, TENANT_ACCESS_BAR_HEIGHT } from './TenantAccessBar';
import { usePresence } from '../../hooks/usePresence';
import { trackAppOpen } from '../../lib/analytics';
import { SubscriptionBanner, useShowSubscriptionBanner, SUBSCRIPTION_BANNER_HEIGHT } from './SubscriptionBanner';
import { PlatformIssueBanner } from '../PlatformIssueBanner';
import { DemoBanner } from './DemoBanner';
import { SurveyPrompt } from '../../features/surveys/SurveyPrompt';
import { GustavoWidget } from '../../features/gustavo/components/GustavoWidget';
import { GustavoOnboardingGate } from '../../features/gustavo/onboarding/GustavoOnboardingGate';
import { AvisoGate } from '../../features/avisos/AvisoGate';
import { BoosterMark } from '../BoosterMark';
import { conmemoracionCrm } from '../../theme/brand';
import { useThemeMode } from '../../theme/ThemeModeContext';
import { useFeatureFlags } from '../../hooks/useFeatureFlag';
import { useAppSelector } from '../../hooks/useAppStore';
import { useSesionDeLaExtension } from '../../features/properties/sesionDeLaExtension';
import { useEffectiveTenant } from '../../hooks/useEffectiveTenant';
import { useChat } from '../../hooks/useChat';
import { AnalyticsTracker } from '../../lib/AnalyticsTracker';
import { DialogScrollbars } from '../ui/DialogScrollbars';
import { PageScrollbar } from '../ui/PageScrollbar';
import { useSupportBugAlerts } from '../../features/support/useSupportBugAlerts';
import { usePhylaxNotifications } from '../../features/phylax/usePhylaxNotifications';
import { LlamadaEnVivo } from '../../features/enVivo/LlamadaEnVivo';
import { LlamadaDelChat } from '../../features/chat/LlamadaDelChat';
import { useAbrirDesdeNotificacion } from '../../hooks/useAbrirDesdeNotificacion';
import { SeccionGuard } from './SeccionGuard';
import { InstalarExtensionAviso } from '../../features/properties/InstalarExtensionAviso';

const SIDEBAR_COLLAPSED_KEY = 'imperium_sidebar_collapsed';
const TOPBAR_HEIGHT = 56;
const IMPERSONATION_BANNER_HEIGHT = 52;

export function DashboardLayout() {
  // Heartbeat de presencia real (base del contador "Agentes en línea" honesto). Solo corre acá,
  // que es el layout autenticado del CRM (detrás de AuthGuard).
  usePresence(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const isDark = mode === 'dark';
  // Toggle claro/oscuro: gate por flag 'theme-toggle' (targeted a QA gustavo@imperium.uy) Y por
  /**
   * El modo claro se abre a TODOS los tenants (20/08/2026, pedido del dueño).
   *
   * Era exclusivo de Imperium porque la variante clara tenía el dorado CABLEADO: cualquier
   * otra marca lo activaba y veía sus botones en dorado. Eso se arregló en `theme/index.ts`
   * —los acentos del claro ahora se derivan de la marca activa—, así que el corte por
   * tenant nativo dejó de tener sentido.
   *
   * Queda el gate por flag, y sigue siendo fail-CLOSED: se exige `=== true` sobre el mapa
   * resuelto, así que apagar `theme-toggle` esconde el botón para todos en ≤30 s.
   */
  const { flags: resolvedFlags } = useFeatureFlags();
  const showThemeToggle = resolvedFlags['theme-toggle'] === true;

  const toggleCollapse = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  const sidebarWidth = sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;
  useChat();
  useSupportBugAlerts(); // No-op para roles distintos a 'booster_support'
  usePhylaxNotifications(); // Snackbars del Agente Virtual (escalación, créditos bajos, visitas)

  // Modo de apertura (app instalada vs navegador) atribuido al agente. En main.tsx
  // también se dispara, pero ahí la sesión puede estar todavía sin token —si el agente
  // acaba de loguearse, este es el que deja el evento con su userId. Se auto-limita a
  // uno por sesión.
  useEffect(() => { trackAppOpen(); }, []);
  // Tocar una push (llamada, mensaje, aviso) lleva a su pantalla, también en el iPhone.
  useAbrirDesdeNotificacion();
  // Para la extensión "Importar a Imperium": así sabe si este agente es de Imperium o de Booster
  // y a qué CRM mandar los avisos. Solo el aviso, ningún dato del usuario. (08/10/2026)
  // ⛔ La 1.3.0 publicada en la Store lo necesita: sin esto el panel le pide al agente que entre al CRM.
  useEffect(() => { window.postMessage({ tipo: 'imperium-crm-sesion' }, window.location.origin); }, []);
  // Desde la 1.5: además le pasa su propio token para el sello de los avisos (ver sesionDeLaExtension).
  useSesionDeLaExtension();

  const impersonating = useAppSelector((s) => !!s.auth.user?.impersonatedBy);
  const userRole      = useAppSelector((s) => s.auth.user?.role);
  const { branding, isActing } = useEffectiveTenant(); // white-label + modo soporte
  /**
   * ⛔ Solo Imperium: esta barra corre en todas las inmobiliarias de la red, y la red es
   * multi-país. La restricción ya vive adentro de `conmemoracionCrm()` —que solo dice que
   * sí cuando el acento que viró es el de Imperium—, así que no se repite el criterio acá.
   */
  const conmemoracionTopbar = conmemoracionCrm();

  const showSubBanner = useShowSubscriptionBanner();
  // Stack vertical de banners fijos bajo el topbar: [topbar] → [impersonation] →
  // [modo soporte, solo dentro de un tenant] → [suscripción]. Cada uno reserva su alto
  // en el topOffset para que el contenido y el sidebar arranquen debajo (si no, quedan tapados).
  const tenantBarTop = TOPBAR_HEIGHT + (impersonating ? IMPERSONATION_BANNER_HEIGHT : 0);
  const subBannerTop = tenantBarTop + (isActing ? TENANT_ACCESS_BAR_HEIGHT : 0);
  const topOffset = subBannerTop + (showSubBanner ? SUBSCRIPTION_BANNER_HEIGHT : 0);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AnalyticsTracker />
      {/* Barra de scroll dorada en TODOS los modales del CRM que desborden (agentes+admins). */}
      <DialogScrollbars />
      {/* Misma barra dorada para el scroll de la PÁGINA: siempre visible, nunca se
          desvanece. La nativa la decide el navegador (Chrome la esconde tras unos
          segundos), y el pedido es que el indicador no se oculte. (Cristian Avila) */}
      <PageScrollbar />

      {/* ── Topbar full-width ── */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: alpha(theme.palette.background.paper, isDark ? 0.85 : 0.92),
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.35)}`,
          zIndex: theme.zIndex.drawer + 1,
          // Hairline emerald al tope (eco del login)
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0, height: '1px',
            background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.7)}, transparent)`,
          },
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: `${TOPBAR_HEIGHT}px !important`,
            /**
             * El 25 de Agosto los botones de la barra van celestes. (pedido del user)
             *
             * Se pinta por CSS sobre los iconos hijos en vez de tocar cada botón: en la
             * barra conviven IconButtons propios, los chips de saldo y la campana, cada uno
             * con su color, y pasarles una prop a todos era tocar cinco componentes para un
             * día. El `!important` es para ganarle al `color` que cada uno trae en su `sx`.
             *
             * ⛔ Se excluyen los que llevan color semántico —un punto rojo de alerta o el
             * verde de un saldo positivo no son decoración de marca— con `data-sin-tenir`.
             */
            ...(conmemoracionTopbar && {
              // El color sale de la variable de marca (que hoy ES el celeste) y no de una
              // constante aparte: así sigue al acento —incluido el tono más oscuro del modo
              // claro— en vez de ser un segundo celeste que hay que mantener a la par.
              '& .MuiIconButton-root .MuiSvgIcon-root:not([data-sin-tenir])': {
                color: 'var(--brand-accent) !important',
              },
            }),
          }}
        >

          {/* Zona izquierda: ancho del sidebar — colapso + logo, alineado con el menú */}
          <Box
            sx={{
              width: { xs: 'auto', md: `${sidebarWidth}px` },
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              pl: 1.5,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            }}
          >
            {/* Hamburguesa mobile — abre el drawer temporal */}
            <IconButton
              aria-label="Abrir menú"
              onClick={() => setSidebarOpen(true)}
              sx={{ display: { md: 'none' }, color: 'text.secondary', flexShrink: 0 }}
            >
              <MenuIcon />
            </IconButton>

            {/* Hamburguesa desktop — togglea el colapso del sidebar */}
            <Tooltip title={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}>
              <IconButton
                aria-label={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
                onClick={toggleCollapse}
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  color: 'text.secondary',
                  borderRadius: 1.5,
                  flexShrink: 0,
                  '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.08) },
                }}
              >
                {sidebarCollapsed ? <MenuIcon /> : <MenuOpen />}
              </IconButton>
            </Tooltip>

            {/* Logo — clickable, dentro del ancho del sidebar (alineado con el menú).
                Colapsado: solo el monograma (la casita) para que entre. */}
            <Box
              component={RouterLink}
              to={userRole === 'booster_admin' ? '/platform/inicio' : userRole === 'booster_support' ? '/support' : '/dashboard'}
              sx={{
                display: 'flex',
                alignItems: 'center', minWidth: 0, ml: 0.5,
                textDecoration: 'none',
                transition: 'opacity 0.15s ease',
                '&:hover': { opacity: 0.85 },
              }}
            >
              <BoosterMark height={30} showWordmark={false} pulse logoUrl={branding?.logoUrl} name={branding?.displayName} />
            </Box>
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* Acciones del topbar */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 1.5 }}>
            <MoneyWalletChip />
            <CreditsWalletChip />
            {/* Toggle modo claro/oscuro — gated por la flag 'theme-toggle' (targeted a
                gustavo@imperium.uy para QA). El modo claro todavía se está puliendo, así que
                fuera del QA nadie lo ve. Cuando esté listo, poner la flag en 'all'. */}
            {showThemeToggle && (
              <Tooltip title={isDark ? 'Modo claro' : 'Modo oscuro'}>
                <IconButton
                  onClick={toggleTheme}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    borderRadius: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: 'primary.main',
                      bgcolor: 'var(--brand-tint)',
                      transform: 'rotate(15deg)',
                    },
                  }}
                >
                  {isDark
                    ? <LightIcon sx={{ fontSize: 19 }} />
                    : <DarkIcon sx={{ fontSize: 19 }} />}
                </IconButton>
              </Tooltip>
            )}
            <NotificationBell />
          </Stack>
        </Toolbar>
      </AppBar>

      {/* ── Banner de impersonation (arriba de todo cuando está activo) ── */}
      <ImpersonationBanner />

      {/* ── Acceso cross-tenant de plataforma (operador/soporte): entrar/salir de un tenant ── */}
      <TenantAccessBar top={tenantBarTop} />

      {/* ── Estado de suscripción (tenants booster): aviso de pago vencido / suspendido ── */}
      <SubscriptionBanner top={subBannerTop} />

      {/* ── Sidebar (arranca debajo del topbar + banner si aplica) ── */}
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleCollapse}
        topOffset={topOffset}
      />

      {/* ── Contenido principal ── */}
      <Box
        component="main"
        // `--crm-top-offset` = dónde termina el header fijo. Lo publicamos como variable
        // porque el alto NO es constante: cambia con los banners de impersonación,
        // acceso a tenant y suscripción. Cualquier pantalla que quiera dejar algo
        // sticky (ej. los filtros del reporte comercial) lo usa en vez de hardcodear 56.
        style={{ '--crm-top-offset': `${topOffset}px` } as React.CSSProperties}
        sx={{
          flexGrow: 1,
          ml: { md: `${sidebarWidth}px` },
          pt: `${topOffset}px`,
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* Banner global de incidencias high/critical — full-width, queda al tope del contenido */}
        <PlatformIssueBanner />

        {/* Barra de "Modo demo" si la sesión es la cuenta demo */}
        <DemoBanner />

        <Box sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2.5, md: 3 },
        }}>
          <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
            <SurveyPrompt />
            {/* Sin wrapper animado: opacity/transform crean stacking context /
                containing block que romperían los overlays fullscreen (showcase). */}
            <SeccionGuard><Outlet /></SeccionGuard>
            {/* El timbre de la llamada interna de una sesión en vivo (Aceptar / Cancelar). */}
            <LlamadaEnVivo />
            {/* La llamada del chat en la que estoy: "Llamando a…" / "En llamada · 02:14" y Cortar. */}
            <LlamadaDelChat />
          </Box>
        </Box>
      </Box>

      {/* Asistente Gustavo — FAB flotante + drawer */}
      <GustavoWidget />

      {/* Onboarding guiado de Gustavo (primera vez, gateado por flag en DB) */}
      <GustavoOnboardingGate />

      {/* Anuncios de una lámina (cambios importantes). Espera a que el onboarding esté
          hecho: los dos overlays juntos son una pared antes de haber entrado. */}
      <AvisoGate />
      <InstalarExtensionAviso />
    </Box>
  );
}
